import { useT } from '@/store';

/**
 * Order-status pill.
 *
 * The grouping changed with the FleetKinetic palette, and not only because
 * the colors did. The old map paired dispatched+loading as amber and
 * en_route+unloading as cyan, which split the five stages down the middle
 * of the sequence rather than by anything a dispatcher acts on. The new
 * pairing is whether the truck is moving:
 *
 *   dispatched, en_route   rolling      → accent (copper)
 *   loading, unloading     standing     → structure (silver)
 *
 * That is the distinction that matters at a glance, because a truck that is
 * standing is the one that can start costing demurrage. It also frees amber
 * to mean only "attention needed" — it is used for expiring inspections and
 * unlinked drivers elsewhere, and a status that is merely normal should not
 * borrow the same color.
 */
const STATUS_CLASS = {
  scheduled: 'bg-white/6 text-dim',
  dispatched: 'bg-accent/14 text-accent',
  loading: 'bg-structure/12 text-structure',
  en_route: 'bg-accent/14 text-accent',
  unloading: 'bg-structure/12 text-structure',
  delivered: 'bg-green/15 text-green',
  cancelled: 'bg-red/15 text-red',
};

export function StatusBadge({ status, live = false }) {
  const t = useT();
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[9.5px] font-bold uppercase tracking-[0.03em] ${
        STATUS_CLASS[status] ?? 'bg-white/6 text-dim'
      }`}
    >
      <span className={`h-1.5 w-1.5 rounded-full bg-current ${live ? 'blink' : ''}`} />
      {t(`status.${status}`)}
    </span>
  );
}

/**
 * The dwell tag: a truck sitting at a quarry past its free allowance. It
 * pulses (a box-shadow ring, not opacity — the map markers already use
 * blinking, and two blinking things next to each other read as noise)
 * because its whole job is to pull a dispatcher's eye to a row they were
 * not looking at.
 *
 * `state: 'unclosed'` is the other thing this row can be, and it gets a
 * quiet amber pill instead. That stop has been open past the engine's
 * plausibility bound, so the truck left at some unknown earlier time and
 * nobody closed the record. Rendering it in red, pulsing, with "+3,780 min"
 * beside it was the bug: the same alarm as a live 21-minute queue, repeated
 * every sixty seconds for as long as the row stayed open, until dispatchers
 * stopped reading dwell tags at all. It does not blink and it does not
 * quote an excess, because there is no measured excess to quote — it says
 * what is actually true, that the record was never closed.
 */
export function DwellBadge({ entry }) {
  const t = useT();
  if (!entry) return null;
  const unclosed = entry.state === 'unclosed';
  const hoursOpen = entry.elapsed_minutes == null ? null : Math.round(entry.elapsed_minutes / 60);
  const title = [
    entry.location_name,
    entry.reason ? t(`dwell.reason.${entry.reason}`) : null,
    unclosed && hoursOpen != null ? t('dwell.openForHours', { n: hoursOpen }) : null,
  ]
    .filter(Boolean)
    .join(' · ');

  if (unclosed) {
    return (
      <span
        className="ml-1 inline-flex items-center gap-1.5 rounded-full bg-amber/18 px-2.5 py-1 text-[9.5px] font-bold uppercase tracking-[0.03em] text-amber"
        title={title}
      >
        <span className="h-1.5 w-1.5 rounded-full bg-current" />
        {t('dwell.unclosedShort')}
      </span>
    );
  }

  return (
    <span
      className="pin-dwell ml-1 inline-flex items-center gap-1.5 rounded-full bg-red/18 px-2.5 py-1 text-[9.5px] font-bold uppercase tracking-[0.03em] text-red"
      title={title}
    >
      <span className="blink h-1.5 w-1.5 rounded-full bg-current" />
      {`⏱ +${entry.excess_minutes ?? '?'} ${t('dispatcher.minShort')}`}
    </span>
  );
}

/**
 * "Nepasiektas" — the driver was assigned this load, and nothing reached
 * them.
 *
 * The board used to show a plain "Išsiųstas" pill whether or not the
 * dispatch message was delivered: notify_driver() returns the channel that
 * got through (or nothing at all), and every call site threw that away. A
 * driver who had never linked Telegram, or whose WhatsApp window had
 * closed, simply never heard about the load — and the only person who could
 * have noticed was looking at a screen that said everything was fine. The
 * truck sat.
 *
 * Shown only for `dispatched`. Once the driver moves the order on, they
 * plainly know about it however the message did or did not arrive, and the
 * badge would be reporting a stale fact about a solved situation.
 * dispatch_notify_attempts = 0 is an order created before any of this
 * existed: nobody recorded an attempt, which is not the same fact as an
 * attempt having failed, so it renders nothing rather than accusing.
 */
export function NotNotifiedBadge({ order }) {
  const t = useT();
  if (!order?.driver_id) return null;
  // V-124. The same test core/dispatch_retry.py's _UNDELIVERED_SQL makes, and
  // it replaced `order_status !== 'dispatched'` here for the same reason: a
  // status move is proof the driver knows, but only the driver who MADE it.
  // dispatched_at is re-stamped whenever the load changes hands, so a
  // loaded_at older than it is the previous driver's tap and says nothing
  // about the one holding the load now.
  if (order.order_status === 'delivered' || order.order_status === 'cancelled') return null;
  if (order.loaded_at && order.loaded_at >= order.dispatched_at) return null;
  if (order.dispatch_notified_via) return null;
  const attempts = order.dispatch_notify_attempts ?? 0;
  if (attempts < 1) return null;

  return (
    <span
      className="ml-1 inline-flex items-center gap-1.5 whitespace-nowrap rounded-full bg-red/18 px-2.5 py-1 text-[9.5px] font-bold uppercase tracking-[0.03em] text-red"
      title={t('orders.notNotifiedTitle', { attempts })}
    >
      <span className="blink h-1.5 w-1.5 rounded-full bg-current" />
      {t('orders.notNotified')}
    </span>
  );
}
